/* eslint global-require: 0, import/newline-after-import: 0 */

import 'location-origin'
require('smoothscroll-polyfill').polyfill()
import 'focus-visible'

import React from 'react'
import ReactDOM from 'react-dom'
import moment from 'moment'
import sha256 from 'js-sha256'
import { getCookieValue } from 'helpers'
import { cleanQueryParams, constants, sentry } from 'common/helpers'
import { getAllQueueProductsArray } from 'common/helpers/getters'
import context from 'common/helpers/analytics/snowplow/context'
import { router } from 'sb-react-router'
import { preloadReady } from 'react-loadable'

import 'scss/fonts/index.scss'


cleanQueryParams()
moment.locale('en-gb')


const getAnalyticsTracker = ({ store, fbq, ga, snaptr, dataLayer }) => {
  let prevPathname

  return (pathname) => {
    if (prevPathname === pathname) {
      return
    }

    const {
      models: {
        me: { email, gender, id },
        subscription: { isCancelled, isSubscribed } = {},
        cart: { items: cartItems = [], totalPrice, totalCount },
      },
    } = store.getState()

    const isMakeupIntroFlowEnabled  = Boolean(getCookieValue(constants.cookieNames.makeupIntroFlow))
    const isSkincareIntroFlow       = Boolean(getCookieValue(constants.cookieNames.skincareIntroFlow))
    const isFreeTrialOrgFlow        = Boolean(getCookieValue(constants.cookieNames.isFreeTrialOrgFlow))

    if (isMakeupIntroFlowEnabled || isSkincareIntroFlow) {
      fbq.trackWithPixel('PageView', constants.facebookPixels.standard)
    }

    if (!isFreeTrialOrgFlow) {
      fbq.track('PageView')
    }

    ga.send('pageview', pathname)
    snaptr.track('PAGE_VIEW')

    let subscriptionStatus = 'subscribed'

    if (isCancelled) {
      subscriptionStatus = 'cancelled'
    }
    else if (!isSubscribed) {
      subscriptionStatus = 'notSubscribed'
    }

    dataLayer.push({
      event: 'pageView',
      user: {
        id,
        gender,
        emailhash: email ? sha256(email) : email,
        email,
        subscriptionStatus,
      },
      revenue: totalPrice / 100,
      cart: JSON.stringify(cartItems),
      count: totalCount,
    })

    if (pathname) {
      prevPathname = pathname
    }
  }
}

const createRouteSwitchHandler = ({ dataLayer }) => {
  let currentMatch

  return (match) => {
    if (!currentMatch || currentMatch.path !== match.path) {
      const { page: pageName } = context.setFromPath(match.path)

      if (pageName) {
        dataLayer.push({ pageName })
      }
    }

    currentMatch = match
  }
}

const handleRouteChange = ({ store, pathname }) => {
  const {
    models: {
      me: { isLoggedIn, id },
      subscription: { status = null, subscriptionDate = null } = {},
      queue: { items: queueMonths },
    },
  } = store.getState()

  const allQueueProducts  = getAllQueueProductsArray(queueMonths, true)
  const queueFullness     = allQueueProducts.length

  if (typeof window.trackSnowplowPageView === 'function') {
    window.trackSnowplowPageView(pathname, {
      isLoggedIn,
      userId: id,
      subscriptionStatus: status,
      subscriptionDateTime: subscriptionDate,
      queueFullness,
    })
  }
  else {
    sentry.captureException('window.trackSnowplowPageView not defined')
  }
}

const render = async (store, storeEvents, translationMessages, isDEV) => {
  const RootProvider      = require('containers/RootProvider/RootProvider').default
  const analytics         = require('common/helpers/analytics')

  // --------------------------------------------------------- /

  const history                 = window.__history__
  const renderAction            = isDEV ? ReactDOM.render : ReactDOM.hydrate
  const tryTrackAnalytics       = getAnalyticsTracker({ store, ...analytics })
  const handleRouteSwitch       = createRouteSwitchHandler(analytics)
  const cosmeticsDataStructure  = store.getState().view.cosmeticsPage.structure

  // --------------------------------------------------------- /

  tryTrackAnalytics(window.location.pathname)

  router.subscribe(handleRouteSwitch)

  history.listen(({ pathname }) => {
    console.log('route changed to:', pathname)

    handleRouteChange({ store, pathname })
    tryTrackAnalytics(pathname)
  })

  if (!isDEV) {
    await preloadReady()
  }

  renderAction(
    <RootProvider {...{ store, storeEvents, history, translationMessages, cosmeticsDataStructure }} />,
    document.getElementById('root')
  )
}

const init = (store, storeEvents, translationMessages, isDEV) => {
  // https://developers.google.com/web/updates/2016/04/intersectionobserver
  const isIntersectionObserverExist = (
    'IntersectionObserver' in window
    && 'IntersectionObserverEntry' in window
    && 'intersectionRatio' in window.IntersectionObserverEntry.prototype
  )

  if (isIntersectionObserverExist) {
    // Minimal polyfill for Edge 15's lack of `isIntersecting`
    // See: https://github.com/w3c/IntersectionObserver/issues/211
    if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
      Object.defineProperty(
        window.IntersectionObserverEntry.prototype,
        'isIntersecting',
        {
          get() {
            return this.intersectionRatio > 0
          },
        }
      )
    }

    render(store, storeEvents, translationMessages, isDEV)
  }
  else {
    require.ensure([ 'sb-intersection-observer/polyfill' ], (require) => {
      require('sb-intersection-observer/polyfill')
      render(store, storeEvents, translationMessages, isDEV)
    })
  }
}


export default init
